import { useEffect, useRef } from 'react'

import { EMPTY_OBJECT } from '../../../constants/common'
import { useApi, useDebouncedState } from '../../../hooks'
import { SEARCH_TIMEOUT } from '../Search.constants'
import { isValidSearchValue } from '../Search.utils'

/**
 * @import {UseApi} from '../../../hooks/useApi'
 */

/**
 * The {@link SearchApiCall} type describes a function to invoke search API.
 *
 * @typedef {(apiConfig?: ApiConfig) => CallApiReturn<TResponse>} SearchApiCall
 * @template TResponse
 */

/**
 * The {@link SearchApi} type describes the return type of
 *    the {@link useSearchApi} hook
 *
 * @typedef {object} SearchApi
 * @template TResponse
 *
 * @property {SearchApiCall<TResponse>} call
 * @property {ApiAbort} abort
 */

/**
 * @template {object} TItem
 *
 * @param {SearchValue} search
 * @param {SearchConfig<TItem> | null} config
 * @param {number} [timeout]
 *
 * @returns {SearchApi<SearchListValue<TItem>> | null}
 */
export function useSearchApi(search, config, timeout = SEARCH_TIMEOUT) {
  const api = useApi()
  const [apiArgs, setApiArgs] = useDebouncedState(null)

  const timeoutRef = useRef(timeout)

  useEffect(() => {
    timeoutRef.current = timeout
  }, [timeout])

  useEffect(() => {
    setApiArgs(
      config && isValidSearchValue(search)
        ? [config.getItems, { config, search }]
        : null,
      timeoutRef.current
    )
  }, [config, search, setApiArgs])

  return apiArgs
    ? {
        ...api,
        call(apiConfig = EMPTY_OBJECT) {
          const [callSearchApi, props] = apiArgs

          return api.call(() => callSearchApi({ ...props, apiConfig }))
        },
      }
    : null
}
